import React from "react";
import { Text, Group, Tooltip } from "@mantine/core";
import { TimezoneUtils } from "../../utils/timezoneUtils";

export interface TimezoneDateDisplayProps {
    /** Timestamp in milliseconds since epoch */
    timestamp: number;
    /** Whether to show date and time or just date */
    showTime?: boolean;
    /** Whether to show the timezone indicator */
    showTimezone?: boolean;
    /** Custom formatting options */
    formatOptions?: Intl.DateTimeFormatOptions;
    /** Text size */
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    /** Text color */
    color?: string;
    /** Whether to show timezone as tooltip instead of inline */
    timezoneAsTooltip?: boolean;
}

/**
 * A reusable component that displays timestamps in the user's timezone
 * with a small timezone indicator
 */
export const TimezoneDateDisplay: React.FC<TimezoneDateDisplayProps> = ({
    timestamp,
    showTime = true,
    showTimezone = true,
    formatOptions,
    size = "sm",
    color,
    timezoneAsTooltip = false
}) => {
    const timezone = TimezoneUtils.getUserTimezone();
    const timezoneAbbr = TimezoneUtils.getTimezoneAbbreviation();

    const formattedDate = showTime ? TimezoneUtils.formatTimestamp(timestamp, formatOptions) : TimezoneUtils.formatDate(timestamp, formatOptions);

    if (!showTimezone) {
        return (
            <Text size={size} c={color}>
                {formattedDate}
            </Text>
        );
    }

    if (timezoneAsTooltip) {
        return (
            <Tooltip label={`Timezone: ${timezone} (${timezoneAbbr})`} position="top" withArrow>
                <Text size={size} c={color} style={{ cursor: "help" }}>
                    {formattedDate}
                </Text>
            </Tooltip>
        );
    }

    return (
        <Group gap={4} wrap="nowrap">
            <Text size={size} c={color}>
                {formattedDate}
            </Text>
            <Text
                size="xs"
                c="dimmed"
                style={{
                    fontSize: "0.65rem",
                    lineHeight: 1,
                    opacity: 0.7
                }}
            >
                {timezoneAbbr}
            </Text>
        </Group>
    );
};
